package com.shadowings.gero.data.repository

import com.shadowings.gero.domain.repository.AuthRepository
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.FirebaseAuth
import dev.gitlive.firebase.auth.FirebaseUser
import dev.gitlive.firebase.auth.auth
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.stateIn

class AuthRepositoryImpl : AuthRepository {
    private val auth: FirebaseAuth get() = Firebase.auth

    // Dispatchers.Main, not Default: Firebase's authStateChanged listener fires from a raw JS
    // callback with zero kotlinx-coroutines dispatcher involvement (see GitLive's callbackFlow +
    // trySend()). Collecting/sharing on Dispatchers.Default lets the StateFlow value update
    // correctly, but on the JS/web target that update doesn't reliably get tied into Compose's
    // own recomposer frame-clock scheduling, so recomposition happens (state is correct) without
    // the canvas ever repainting until something else (e.g. a nav-triggered layout pass) forces
    // a frame. Main is the dispatcher Compose's own recomposer is driven by.
    private val scope = CoroutineScope(Dispatchers.Main)

    // Shared, eagerly-started so every consumer (Landing, Profile, CloudSync, ...) reads the
    // same live Firebase auth listener instead of each registering its own on first
    // subscription — a fresh listener on web can lag behind Firebase's async IndexedDB session
    // restore, making auth state look stale until something else happens to re-subscribe.
    override val currentUser: Flow<FirebaseUser?> = auth.authStateChanged
        .onEach {
            com.shadowings.gero.util.requestUiRepaint()
        }
        .stateIn(scope, SharingStarted.Eagerly, auth.currentUser)

    override suspend fun signInWithEmail(email: String, password: String): Result<FirebaseUser> {
        return try {
            val result = auth.signInWithEmailAndPassword(email, password)
            val user = result.user ?: throw Exception("Utente non trovato dopo il login")
            Result.success(user)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun signUpWithEmail(email: String, password: String): Result<FirebaseUser> {
        return try {
            val result = auth.createUserWithEmailAndPassword(email, password)
            val user = result.user ?: throw Exception("Utente non trovato dopo la registrazione")
            Result.success(user)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun signInWithGoogle(): Result<FirebaseUser> {
        return try {
            com.shadowings.gero.util.GoogleSignInHelper().signIn()
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun signOut(): Result<Unit> {
        return try {
            auth.signOut()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}
